import {
  DENTAL_LOC_PRODUCT_TYPE,
  toV5CoverageType,
  type Proposal,
} from "@platform/mpx-quotes-grid";
import { CoverageTypeEnum } from "@platform/types";

// Per LOC (v5 coverage type), the union of every proposal's requestedProductPlanTypes for
// that coverage: `null` means at least one proposal requested the whole coverage (the
// broadest state, winning outright), and a string array is the union of the specific product
// types any carrier was asked for. A LOC absent from the map never appeared in any proposal
// (e.g. before the RFP has been sent) and is treated as unfiltered by the caller.
const requestedProductPlanTypesByLOC = (
  proposals: readonly Proposal[],
): Map<string, string[] | null> =>
  proposals.reduce<Map<string, string[] | null>>((byLOC, proposal) => {
    proposal.coverages.forEach((coverage) => {
      const existing = byLOC.get(coverage.coverageType);
      if (existing === null) return; // whole coverage already won for this LOC

      const requested = coverage.requestedProductPlanTypes;
      // A proposal predating this field (or a fixture built before it existed) carries
      // `undefined` here despite the `string[] | null` domain type — fall back to the
      // same unfiltered treatment as an explicit `null` rather than spreading a
      // non-array below.
      if (!Array.isArray(requested)) {
        byLOC.set(coverage.coverageType, null);
        return;
      }

      byLOC.set(
        coverage.coverageType,
        Array.from(new Set([...(existing ?? []), ...requested])),
      );
    });

    return byLOC;
  }, new Map());

/**
 * Narrows a list of (possibly frontend-extended) coverage types to those requested of at
 * least one carrier proposal on the RFP — the union of every proposal's
 * `requestedProductPlanTypes` for that LOC (CD-2371). The frontend splits dental into
 * DENTAL_PPO/DENTAL_HMO for UI routing, and a product-type-specific request (dental only,
 * per BTX's CONTEXT.md) already resolves to distinct entries here, so no separate
 * per-product-type pass is needed beyond this LOC-level filter.
 *
 * A LOC no proposal has ever mentioned (before the RFP has been sent, or a legacy proposal
 * predating this field) stays unfiltered — the safe default, matching the backend's own
 * `requestedProductPlanTypes` semantics.
 */
export const filterToRequestedCoverageTypes = (
  coverageTypes: readonly CoverageTypeEnum[],
  proposals: readonly Proposal[],
): CoverageTypeEnum[] => {
  const requestedByLOC = requestedProductPlanTypesByLOC(proposals);

  return coverageTypes.filter((coverageType) => {
    const requested = requestedByLOC.get(toV5CoverageType(coverageType));
    const wholeCoverageOrUnknown =
      requested === undefined || requested === null;

    if (wholeCoverageOrUnknown) return true;

    // A non-nil, empty set means the LOC was excluded from the send entirely — true
    // regardless of any dental product-type split, so this must be checked before falling
    // back to "no product-type distinction for this LOC, so keep it" below.
    if (requested.length === 0) return false;

    const expectedProductType = DENTAL_LOC_PRODUCT_TYPE[coverageType];
    if (!expectedProductType) return true;

    return requested.some(
      (productType) => productType.toLowerCase() === expectedProductType,
    );
  });
};
